import * as React from 'react'

import { XIcon } from 'lucide-react'

import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'

interface IProps {
  open: boolean
  name: string
  /** Falls back to a muted box when omitted. */
  filledImage?: string
  /** Project-location for unassigned maps; undefined skips the LBS chip. */
  locationAttribute?: LocationBase
  onApply: () => void
  onCancel: () => void
  isDeleting?: boolean
  /** Required when nesting inside a focus-trapping ancestor so Radix considers the dialog "inside". */
  portalContainer?: React.RefObject<HTMLElement | null>
}

export const DeleteMapViewConfirmDialog: React.FC<IProps> = ({
  open,
  name,
  filledImage,
  locationAttribute,
  onApply,
  onCancel,
  isDeleting = false,
  portalContainer,
}) => (
  <Dialog
    open={open}
    onOpenChange={next => {
      if (!next) onCancel()
    }}
  >
    <DialogContent
      className="tw:max-w-[420px]"
      portalContainer={portalContainer}
      // Product rule: close (X) goes top-LEFT; suppress shadcn's default top-right X.
      showCloseButton={false}
    >
      <button
        type="button"
        onClick={onCancel}
        aria-label={Localization.translator.cancel}
        className="tw:absolute tw:top-2 tw:left-2 tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:bg-transparent tw:p-0 tw:text-muted-foreground tw:cursor-pointer tw:outline-none tw:transition-colors tw:hover:bg-muted tw:focus-visible:bg-muted"
      >
        <XIcon className="tw:size-4" aria-hidden="true" />
      </button>
      <DialogHeader className="tw:pl-8">
        <DialogTitle>
          {Localization.translator.areYouSureDeleteMapView}
        </DialogTitle>
      </DialogHeader>
      <div className="tw:flex tw:min-w-0 tw:items-start tw:gap-4 tw:py-2">
        <div className="tw:size-24 tw:shrink-0 tw:overflow-hidden tw:rounded-md tw:bg-muted">
          {filledImage && (
            <img
              src={filledImage}
              alt={name}
              className="tw:block tw:h-full tw:w-full tw:object-cover"
              draggable={false}
            />
          )}
        </div>
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
          <div
            className="tw:truncate tw:text-title-md tw:text-foreground"
            title={name}
          >
            {name}
          </div>
          {locationAttribute && (
            <SitemapAttributeTag
              dataObject={locationAttribute}
              shouldShowAsTag={true}
              contentContainerClassName="text-ellipsis py2"
            >
              <span title={locationAttribute.name}>
                {locationAttribute.name}
              </span>
            </SitemapAttributeTag>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          size="lg"
          onClick={onCancel}
          disabled={isDeleting}
        >
          {Localization.translator.cancel}
        </Button>
        <Button
          variant="destructive"
          size="lg"
          onClick={onApply}
          disabled={isDeleting}
        >
          {isDeleting && (
            <span data-icon="inline-start" className="tw:inline-flex">
              <Loader />
            </span>
          )}
          {Localization.translator.delete}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
